import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import { listAdminBookings } from "../Venues/availabilityApi";
import { CalendarDays } from "lucide-react";

const AdminBookingsList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = location.pathname.startsWith("/venue-admin")
    ? "/venue-admin"
    : location.pathname.startsWith("/country-admin")
    ? "/country-admin"
    : "/super-admin";

  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "blocked" });

  // ✅ useCallback so ESLint is happy and deps are correct
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAdminBookings({
        status: filters.status === "all" ? undefined : filters.status,
      });
      setRaw(rows);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to load admin bookings",
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ✅ single effect, re-runs whenever `load` changes (i.e. when filters change)
  useEffect(() => {
    load();
  }, [load]);

  const tableItems = useMemo(() => {
    return (raw || []).map((r) => ({
      ...r,
      dateStr: r?.date || "—",
      slotStr:
        r?.startTime && r?.endTime ? `${r.startTime}-${r.endTime}` : "—",
      payer: "—", // keep columns identical to User table
      phone: "—",
      amount: 0, // show 0 / dash; there’s no price for blocks
      status: r?.status || "blocked",
      createdOn: r?.createdAt ? new Date(r.createdAt).toLocaleString() : "—",
    }));
  }, [raw]);

  const filterDefs = useMemo(
    () => [
      {
        id: "status",
        label: "Status",
        options: [
          { value: "blocked", label: "Blocked" },
          { value: "open", label: "Open" },
          { value: "all", label: "All" },
        ],
      },
    ],
    [],
  );

  const tc = useTableController(tableItems, {
    searchKeys: [
      "venueName",
      "groundName",
      "dateStr",
      "slotStr",
      "createdOn",
      "note",
    ],
    initialPageSize: 10,
  });

  const serialStart = (tc.page - 1) * tc.pageSize;

  return (
    <>
      {/* Tabs */}
      <div className="sa-tabbar" style={{ marginBottom: 12 }}>
        <NavLink
          to={`${basePath}/admin-bookings`}
          className={({ isActive }) =>
            isActive ? "sa-tab sa-tab--active" : "sa-tab"
          }
        >
          Admin Bookings
        </NavLink>
        <NavLink to={`${basePath}/reservations`} className="sa-tab">
          User Bookings
        </NavLink>
      </div>

      <div className="sa-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Admin Bookings</h1>
      </div>

      <div className="sa-toolbar-wrap" style={{ "--sa-search-width": "440px" }}>
        <TableToolbar
          searchTerm={tc.searchTerm}
          onSearchChange={tc.setSearch}
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(id, val) =>
            setFilters((p) => ({ ...p, [id]: val }))
          }
          searchPlaceholder="Search admin bookings…"
        />
        <div className="sa-toolbar-icons">
          <button
            type="button"
            className="sa-btn-icon"
            title="Upcoming (grouped)"
            onClick={() => navigate(`${basePath}/reservations/upcoming`)}
          >
            <CalendarDays className="lucide" />
          </button>
        </div>
      </div>

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.rows.length === 0 ? (
          <div className="sa-empty">No admin bookings found.</div>
        ) : (
          <>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <th>Player</th>
                    <th>Phone</th>
                    <th>Venue / Ground</th>
                    <th>Date</th>
                    <th>Slots</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right", width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tc.rows.map((r, i) => (
                    <tr key={r._id}>
                      <td>{serialStart + i + 1}</td>
                      <td>{r.payer}</td>
                      <td>{r.phone}</td>
                      <td>
                        <div className="sa-compact">
                          <div>{r.venueName || "—"}</div>
                          <small className="sa-gray">
                            {r.groundName || "—"}
                          </small>
                        </div>
                      </td>
                      <td>{r.dateStr}</td>
                      <td>{r.slotStr}</td>
                      <td>—</td>
                      <td>
                        <span className={`sa-badge sa-badge--${r.status}`}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {/* read-only for now */}
                        <span className="sa-gray">—</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </>
        )}
      </div>
    </>
  );
};

export default AdminBookingsList;
